/* Extracted from BulkSimilarityPage.jsx — see git log for history. */
import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import {
  AlertTriangle,
  CheckCircle2,
  GitMerge,
  SkipForward,
  Send,
  X,
  Info,
  ArrowRight,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { buildApplyPlan } from './buildApplyPlan';

// ============================================================================
// MergePreviewModal — full dry-run of the pending Apply.
//
// Renders the exact 4-step execution order that executeApply will run:
//   1. Merge (staging → staging)   ← runs first
//   2. Merge (staging → approved)
//   3. Skip (soft-delete)
//   4. Keep (promote to approved DB) ← runs last
//
// Cross-reference warnings surface rows that are both merge targets AND
// have their own Keep/Skip action — the exact class of interaction that
// caused the original "staging target auto-approved" bug.
//
// Cycles are terminal: Confirm is disabled until the admin breaks them.
// ============================================================================
const stepStyles = {
  'merge-staging': {
    icon: GitMerge,
    color: 'emerald',
    hint: 'Consolidates history — sources absorbed into targets, then deleted from staging.',
  },
  'merge-approved': {
    icon: GitMerge,
    color: 'teal',
    hint: 'Sources absorbed into the approved-DB target; sources deleted from staging.',
  },
  skip: {
    icon: SkipForward,
    color: 'red',
    hint: 'Moved to Trash — recoverable for 7 days.',
  },
  keep: {
    icon: Send,
    color: 'indigo',
    hint: 'Promoted to the live questions DB with any absorbed asked_by_history.',
  },
};

const colorClasses = {
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  teal: 'bg-teal-50 border-teal-200 text-teal-800',
  red: 'bg-red-50 border-red-200 text-red-800',
  indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
};

const badgeClasses = {
  emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  teal: 'bg-teal-100 text-teal-700 border-teal-200',
  red: 'bg-red-100 text-red-700 border-red-200',
  indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
};

const RowChip = ({ row, tone = 'gray' }) => {
  if (!row) return null;
  const cls =
    tone === 'target'
      ? 'bg-emerald-100 text-emerald-900 border-emerald-200'
      : tone === 'approved'
      ? 'bg-teal-100 text-teal-900 border-teal-200'
      : 'bg-gray-100 text-gray-900 border-gray-200';
  const label = row.isApproved
    ? row.text
    : row.rowNumber != null
    ? `Row ${row.rowNumber}${row.isCrossBatchMatch && row.sourceFilename ? ` · ${row.sourceFilename}` : ''}`
    : row.isCrossBatchMatch
    ? `Cross-batch match${row.sourceFilename ? ` · ${row.sourceFilename}` : ''}`
    : `${(row.id || '').slice(0, 12)}…`;
  const preview = row.isApproved ? '' : (row.text || '').slice(0, 60);
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] ${cls}`}>
      <span className="font-semibold">{label}</span>
      {preview && (
        <span className="text-gray-600 truncate max-w-[280px]" title={row.text}>
          — {preview}
          {(row.text || '').length > 60 ? '…' : ''}
        </span>
      )}
    </span>
  );
};

// Steps with more than this many ops auto-collapse — a first-N preview
// is shown with a "Show all N ops" toggle to expand. Keeps the modal
// usable on batches with hundreds of rows without falling back to a
// giant scroll wall. Threshold picked empirically: ≤20 fits comfortably
// on a laptop viewport without collapsing.
const AUTO_COLLAPSE_THRESHOLD = 20;
const COLLAPSED_PREVIEW_COUNT = 5;

const OpLine = ({ op }) => (
  <li className="text-[11px] flex items-center flex-wrap gap-1">
    {op.kind === 'merge-staging' && (
      <>
        <RowChip row={op.source} />
        <ArrowRight className="w-3 h-3 text-gray-500 shrink-0" />
        <RowChip row={op.target} tone="target" />
      </>
    )}
    {op.kind === 'merge-approved' && (
      <>
        <RowChip row={op.source} />
        <ArrowRight className="w-3 h-3 text-gray-500 shrink-0" />
        <RowChip row={op.target} tone="approved" />
      </>
    )}
    {op.kind === 'skip' && <RowChip row={op.row} />}
    {op.kind === 'keep' && <RowChip row={op.row} />}
  </li>
);

const StepBlock = ({ step, index }) => {
  const meta = stepStyles[step.kind];
  const Icon = meta.icon;
  const shouldCollapse = step.ops.length > AUTO_COLLAPSE_THRESHOLD;
  const [expanded, setExpanded] = useState(!shouldCollapse);
  if (step.ops.length === 0) return null;
  const visibleOps = expanded
    ? step.ops
    : step.ops.slice(0, COLLAPSED_PREVIEW_COUNT);
  const hiddenCount = step.ops.length - visibleOps.length;
  return (
    <div
      className={`border rounded p-3 ${colorClasses[meta.color]}`}
      data-testid={`plan-step-${step.kind}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" />
          <span className="text-sm font-semibold">
            Step {index} · {step.label}
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${badgeClasses[meta.color]}`}>
            {step.ops.length} op{step.ops.length !== 1 ? 's' : ''}
          </span>
          {step.runsFirst && (
            <span className="text-[10px] text-gray-500 italic">runs first</span>
          )}
          {step.runsLast && (
            <span className="text-[10px] text-gray-500 italic">runs last</span>
          )}
        </div>
        {shouldCollapse && (
          <button
            onClick={() => setExpanded((e) => !e)}
            className="text-[11px] font-medium text-gray-700 hover:text-gray-900 flex items-center gap-1"
            data-testid={`plan-step-${step.kind}-toggle`}
          >
            {expanded ? (
              <>
                Collapse <ChevronUp className="w-3 h-3" />
              </>
            ) : (
              <>
                Show all {step.ops.length} <ChevronDown className="w-3 h-3" />
              </>
            )}
          </button>
        )}
      </div>
      <ul className="space-y-1 pl-6">
        {visibleOps.map((op, i) => (
          <OpLine key={`${op.kind}-${op.row?.rowNumber ?? op.row?.id ?? i}`} op={op} />
        ))}
        {hiddenCount > 0 && (
          <li className="text-[10px] text-gray-500 italic pl-1">
            … and {hiddenCount} more (click &quot;Show all&quot; above)
          </li>
        )}
      </ul>
      <p className="text-[10px] text-gray-600 mt-2 italic pl-6">{meta.hint}</p>
    </div>
  );
};

const WarningLine = ({ w }) => {
  if (w.kind === 'target-also-keep') {
    return (
      <li className="text-[11px] leading-relaxed" data-testid="warning-target-also-keep">
        <RowChip row={w.target} tone="target" />
        <span className="mx-1">is a merge target AND is marked</span>
        <span className="px-1 py-0.5 bg-indigo-100 text-indigo-800 rounded text-[10px] font-semibold">
          Keep
        </span>
        <span className="mx-1">
          — will be promoted to the live DB <em>after</em> absorbing{' '}
          {w.sources.map((s, i) => (
            <React.Fragment key={s.rowNumber ?? i}>
              {i > 0 && ', '}
              Row {s.rowNumber ?? '—'}
            </React.Fragment>
          ))}
          . Full history preserved. ✓
        </span>
      </li>
    );
  }
  if (w.kind === 'target-also-skip') {
    return (
      <li className="text-[11px] leading-relaxed" data-testid="warning-target-also-skip">
        <RowChip row={w.target} tone="target" />
        <span className="mx-1">is a merge target AND is marked</span>
        <span className="px-1 py-0.5 bg-red-100 text-red-800 rounded text-[10px] font-semibold">
          Delete
        </span>
        <span className="mx-1">
          — will absorb{' '}
          {w.sources.map((s, i) => (
            <React.Fragment key={s.rowNumber ?? i}>
              {i > 0 && ', '}
              Row {s.rowNumber ?? '—'}
            </React.Fragment>
          ))}
          then be soft-deleted to Trash (recoverable for 7 days).
        </span>
      </li>
    );
  }
  return null;
};

const MergePreviewModal = ({ preview, rowState, allRows, onCancel, onConfirm }) => {
  const { skipIds, keepIds = [], analysis } = preview;
  const plan = buildApplyPlan({ skipIds, keepIds, analysis, rowState, allRows });
  const { steps, warnings, cycles, chains, hasErrors, totalOps } = plan;

  // Helper for cycle rendering (retained from prior version)
  const labelFor = (sid) => {
    const r = (allRows || []).find((x) => x.staging_question_id === sid);
    if (!r) return sid;
    const text = (r.question_text || '').slice(0, 40);
    return `Row ${r.row_number}: ${text}${(r.question_text || '').length > 40 ? '…' : ''}`;
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      data-testid="merge-preview-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`px-5 py-3 border-b ${
            hasErrors ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2
                className={`text-base font-semibold ${
                  hasErrors ? 'text-red-900' : 'text-slate-900'
                }`}
              >
                {hasErrors
                  ? 'Merge cycle detected — please resolve'
                  : 'Apply Preview'}
              </h2>
              <p
                className={`text-xs mt-0.5 ${
                  hasErrors ? 'text-red-700' : 'text-slate-600'
                }`}
              >
                {hasErrors
                  ? 'A merge cycle would cause data loss. Fix it by changing one of the rows below to Keep or to a different target.'
                  : `${totalOps} action${totalOps !== 1 ? 's' : ''} will run in the order shown below.`}
              </p>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-900"
              data-testid="merge-preview-close-btn"
              aria-label="Close preview"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Sticky summary strip — always visible even when scrolled through
            a long step. Compact chip layout scales gracefully to 4-digit
            counts. Hidden when there's nothing to summarise. */}
        {!hasErrors && (totalOps > 0 || warnings.length > 0) && (
          <div
            className="px-5 py-2 bg-white border-b border-gray-200 flex items-center gap-2 flex-wrap text-[11px]"
            data-testid="apply-preview-summary-strip"
          >
            {steps.map((s) => {
              if (s.ops.length === 0) return null;
              const meta = stepStyles[s.kind];
              const Icon = meta.icon;
              return (
                <span
                  key={s.kind}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${badgeClasses[meta.color]}`}
                >
                  <Icon className="w-3 h-3" />
                  <span className="font-semibold">{s.ops.length}</span>
                  <span className="opacity-75">
                    {s.kind === 'merge-staging' && 'merge (staging)'}
                    {s.kind === 'merge-approved' && 'merge (approved)'}
                    {s.kind === 'skip' && 'skip'}
                    {s.kind === 'keep' && 'keep'}
                  </span>
                </span>
              );
            })}
            {warnings.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-amber-50 border-amber-200 text-amber-800">
                <AlertTriangle className="w-3 h-3" />
                <span className="font-semibold">{warnings.length}</span>
                <span className="opacity-75">
                  note{warnings.length !== 1 ? 's' : ''}
                </span>
              </span>
            )}
            <span className="ml-auto text-gray-500">
              <span className="font-semibold text-gray-800">{totalOps}</span> total
            </span>
          </div>
        )}

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto space-y-3">
          {/* Cycles — terminal error */}
          {cycles.length > 0 && (
            <div
              className="border border-red-300 bg-red-50/60 rounded p-3"
              data-testid="merge-cycles-section"
            >
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-700" />
                <span className="text-sm font-semibold text-red-900">
                  {cycles.length} cycle{cycles.length !== 1 ? 's' : ''}
                </span>
              </div>
              <ul className="space-y-2 text-xs text-red-900">
                {cycles.map((cyc, i) => (
                  <li key={cyc.join('→') || i} className="font-mono">
                    {cyc.map((sid) => (
                      <span key={sid}>
                        <span className="bg-red-100 px-1 rounded">{labelFor(sid)}</span>
                        {j < cyc.length - 1 && (
                          <span className="text-red-500 mx-1">→</span>
                        )}
                      </span>
                    ))}
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-red-700 mt-2 italic">
                Apply is disabled while a cycle exists. Close this dialog, change
                one row&apos;s action/target on the page, then click Apply again.
              </p>
            </div>
          )}

          {/* Chain summary (transitive merges) */}
          {!hasErrors && chains.length > 0 && (
            <div
              className="border border-indigo-300 bg-indigo-50/50 rounded p-3"
              data-testid="merge-chains-section"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Info className="w-4 h-4 text-indigo-700" />
                <span className="text-sm font-semibold text-indigo-900">
                  {chains.length} transitive chain{chains.length !== 1 ? 's' : ''}
                </span>
              </div>
              <ul className="space-y-1.5 text-[11px] text-indigo-900">
                {chains.map((ch, i) => (
                  <li key={ch.path.join('→') || i}>
                    {ch.path.map((sid, j) => (
                      <span key={`${sid}-${j}`}>
                        <span
                          className={`px-1 rounded ${
                            j === ch.path.length - 1
                              ? 'bg-emerald-100 font-semibold'
                              : 'bg-indigo-100'
                          }`}
                        >
                          {labelFor(sid)}
                        </span>
                        {j < ch.path.length - 1 && (
                          <span className="text-indigo-500 mx-1">→</span>
                        )}
                      </span>
                    ))}
                    {ch.finalTargetSource === 'approved' && (
                      <span className="text-indigo-500 mx-1">
                        →{' '}
                        <span className="bg-emerald-100 px-1 rounded font-semibold">
                          Approved Q ({ch.finalTarget.slice(0, 24)}…)
                        </span>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="text-[10px] text-indigo-700 mt-1.5 italic">
                Chains are executed in dependency order — inner merges land
                before outer ones so no history is lost.
              </p>
            </div>
          )}

          {/* Cross-reference warnings — the interaction that caused the bug */}
          {!hasErrors && warnings.length > 0 && (
            <div
              className="border border-amber-300 bg-amber-50/60 rounded p-3"
              data-testid="apply-preview-warnings"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <AlertTriangle className="w-4 h-4 text-amber-700" />
                <span className="text-sm font-semibold text-amber-900">
                  {warnings.length} note{warnings.length !== 1 ? 's' : ''} on target rows
                </span>
              </div>
              <ul className="space-y-1 text-amber-900">
                {warnings.map((w, i) => (
                  <WarningLine key={`${w.kind}-${w.target?.rowNumber ?? i}`} w={w} />
                ))}
              </ul>
            </div>
          )}

          {/* The 4-step ordered plan */}
          {!hasErrors && totalOps > 0 && (
            <div className="space-y-2" data-testid="apply-preview-plan">
              {steps.map((s, idx) => (
                <StepBlock key={s.kind} step={s} index={idx + 1} />
              ))}
            </div>
          )}

          {/* Empty state */}
          {!hasErrors && totalOps === 0 && (
            <div
              className="text-center py-8 text-sm text-gray-500"
              data-testid="apply-preview-empty"
            >
              No actions to apply. Tag at least one row as Keep, Merge, or Delete
              first.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            data-testid="merge-preview-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={hasErrors || totalOps === 0}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            onClick={onConfirm}
            data-testid="merge-preview-confirm-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-1" />
            {hasErrors
              ? 'Fix the cycle first'
              : totalOps === 0
              ? 'Nothing to apply'
              : `Confirm & Apply (${totalOps})`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MergePreviewModal;
